import axios, { AxiosInstance } from 'axios'
import { msalInstance } from '../auth/msalInstance'
import { apiScopes } from '../auth/authConfig'

const API_URL = import.meta.env.VITE_API_URL || ''

async function getAccessToken(): Promise<string | null> {
  const accounts = msalInstance.getAllAccounts()
  console.log('[API] MSAL accounts:', accounts.length, accounts.map(a => a.username))

  if (accounts.length === 0) {
    console.log('[API] No accounts found, returning null token')
    return null
  }

  try {
    console.log('[API] Acquiring token for account:', accounts[0].username)
    const response = await msalInstance.acquireTokenSilent({
      ...apiScopes,
      account: accounts[0],
    })
    console.log('[API] Token acquired successfully')
    return response.accessToken
  } catch (error) {
    console.error('[API] Token acquisition failed:', error)
    return null
  }
}

const api: AxiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 300000, // 5 minutes default timeout
})

api.interceptors.request.use(async (config) => {
  const token = await getAccessToken()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
    console.log('[API] Request with auth token to:', config.url)
  } else {
    console.warn('[API] Request WITHOUT auth token to:', config.url)
  }
  return config
})

// Types
export interface Project {
  id: string
  customer_id: string
  customer_name: string
  project_name: string
  description?: string
  status?: 'active' | 'processing' | 'completed'
  workflow_type?: 'delivery' | 'pre_sales'
  recording_count: number
  document_count: number
  created_at: string
  updated_at: string
}

export interface Recording {
  id: string
  project_id: string
  filename: string
  original_filename: string
  blob_url: string
  file_size: number
  duration_seconds?: number
  workshop_type: string
  notes?: string
  status: 'uploaded' | 'transcribing' | 'transcribed' | 'processing' | 'ready' | 'error'
  error_message?: string
  transcript_id?: string
  created_at: string
}

export interface LegalReviewFinding {
  category: 'scope_clarity' | 'legal_liability' | 'ambiguous_language' | 'compliance_risk'
  severity: 'critical' | 'high' | 'medium' | 'low' | 'info'
  title: string
  description: string
  location: string
  recommendation: string
  suggested_text?: string | null
}

export interface LegalReviewData {
  model_used: string
  overall_assessment: 'PASS' | 'NEEDS_REVISION' | 'HIGH_RISK' | 'ERROR'
  findings: LegalReviewFinding[]
  scope_assessment: {
    in_scope_clarity_score: number
    out_scope_clarity_score: number
    completeness_score: number
    comments: string
  }
  liability_risks: Array<{
    risk_type: string
    description: string
    mitigation: string
  }>
  error?: string
}

export interface Document {
  id: string
  project_id: string
  template_name: string
  output_format: string
  blob_url: string
  file_size: number
  status: 'pending' | 'generating' | 'completed' | 'error'
  error_message?: string
  created_at: string
  completed_at?: string
  legal_review_status?: 'reviewing' | 'completed' | 'error' | null
  legal_review_data?: LegalReviewData | null
  legal_review_requested_at?: string | null
  legal_review_completed_at?: string | null
}

export interface QueryResponse {
  id: string
  project_id: string
  question: string
  answer: string
  sources: Array<{
    recording_id: string
    recording_filename: string
    timestamp_start: number
    timestamp_end: number
    text_snippet: string
  }>
  document_sources?: DocumentQuerySource[]
  created_at: string
}

export interface TeamsMeeting {
  id: string
  subject: string
  start?: string
  end?: string
  join_url: string
  organizer?: string
  has_transcript: boolean
  transcript_count: number
}

export interface TeamsTranscript {
  id: string
  meeting_id: string
  created_at?: string
}

export interface TeamsImportResponse {
  recording_id: string
  transcript_id: string
  message: string
}

export type TemplateFileType = 'markdown' | 'docx'
export type UseCase =
  | 'caf' | 'waf' | 'aidw' | 'security-assessment' | 'maturity-assessment'  // Frameworks
  | 'azure' | 'm365' | 'paloalto' | 'citrix' | 'vdi' | 'nutanix' | 'networking'  // Technology
  | 'sow' | 'wbs' | 'proposal' | 'discovery' | 'pricing' | 'design' | 'migration' | 'implementation' | 'runbook' | 'training'  // Doc Types
  | 'managed-services' | 'optimization' | 'modernization'  // Services
  | 'other'  // Default

export const USE_CASE_OPTIONS: { value: UseCase; label: string; group: string }[] = [
  // Frameworks & Assessments
  { value: 'caf', label: 'CAF (Cloud Adoption Framework)', group: 'Frameworks' },
  { value: 'waf', label: 'WAF (Well-Architected Framework)', group: 'Frameworks' },
  { value: 'aidw', label: 'AIDW (AI Data Workshop)', group: 'Frameworks' },
  { value: 'security-assessment', label: 'Security Assessment', group: 'Frameworks' },
  { value: 'maturity-assessment', label: 'Maturity Assessment', group: 'Frameworks' },
  // Technology-Specific
  { value: 'azure', label: 'Azure', group: 'Technology' },
  { value: 'm365', label: 'Microsoft 365', group: 'Technology' },
  { value: 'paloalto', label: 'Palo Alto', group: 'Technology' },
  { value: 'citrix', label: 'Citrix', group: 'Technology' },
  { value: 'vdi', label: 'VDI / Virtual Desktop', group: 'Technology' },
  { value: 'nutanix', label: 'Nutanix', group: 'Technology' },
  { value: 'networking', label: 'Networking', group: 'Technology' },
  // Document Types
  { value: 'sow', label: 'Statement of Work', group: 'Document Types' },
  { value: 'proposal', label: 'Proposal', group: 'Document Types' },
  { value: 'design', label: 'Design / Architecture', group: 'Document Types' },
  { value: 'migration', label: 'Migration Plan', group: 'Document Types' },
  { value: 'implementation', label: 'Implementation Guide', group: 'Document Types' },
  { value: 'runbook', label: 'Runbook', group: 'Document Types' },
  { value: 'training', label: 'Training Material', group: 'Document Types' },
  // Service Categories
  { value: 'managed-services', label: 'Managed Services', group: 'Services' },
  { value: 'optimization', label: 'Optimization', group: 'Services' },
  { value: 'modernization', label: 'Modernization', group: 'Services' },
  // Default
  { value: 'other', label: 'Other', group: 'Other' },
]

export interface Template {
  id: string
  name: string
  display_name: string
  description: string
  content: string
  file_type: TemplateFileType
  blob_url?: string
  file_size: number
  category: string
  use_case: UseCase
  is_active: boolean
  created_by?: string
  created_at: string
  updated_at: string
}

export type TemplateStatus = 'draft' | 'published'

export interface TemplateListItem {
  id: string
  name: string
  display_name: string
  description: string
  file_type: TemplateFileType
  category: string
  use_case: UseCase
  is_active: boolean
  status: TemplateStatus
  version?: number
  source_aiify_job_id?: string
  created_at: string
  updated_at: string
}

export interface TemplateCreate {
  name: string
  display_name: string
  description?: string
  content: string
  category?: string
  use_case?: UseCase
}

export type ProjectDocumentStatus = 'uploaded' | 'processing' | 'indexed' | 'error'

export interface ProjectDocument {
  id: string
  project_id: string
  filename: string
  original_filename: string
  blob_url: string
  file_size: number
  file_type: string
  doc_category: string
  status: ProjectDocumentStatus
  error_message?: string
  chunk_count: number
  created_at: string
  updated_at: string
}

export interface DocumentQuerySource {
  document_id: string
  document_filename: string
  page_number?: number
  text_snippet: string
  relevance_score: number
}

// GeekMode Types
export type ExecutionStatus = 'pending' | 'running' | 'completed' | 'error'

export interface PromptExecution {
  id: string
  document_id?: string
  project_id: string
  section_name?: string
  prompt_text: string
  response_text?: string
  model?: string
  tokens_input?: number
  tokens_output?: number
  duration_ms?: number
  status: ExecutionStatus
  error_message?: string
  // Prompt library tracking
  prompt_id?: string
  prompt_name?: string
  prompt_version_id?: string
  prompt_version_number?: number
  prompt_source?: 'library' | 'experiment' | 'inline'
  // A/B experiment tracking
  experiment_id?: string
  experiment_name?: string
  variant_id?: string
  variant_name?: string
  created_at: string
}

export interface GeekModeStats {
  total_executions: number
  total_tokens_input: number
  total_tokens_output: number
  total_duration_ms: number
  executions_by_status: Record<string, number>
}

// AI Model Types
export type AIModelProvider =
  | 'openai-gpt4o'
  | 'openai-gpt4o-mini'
  | 'claude-sonnet-4-5'
  | 'claude-opus-4-5'
  | 'claude-haiku-4-5'
  | 'claude-opus-4-1'
  | 'document-intelligence'

export interface AIModelInfo {
  name: string
  provider: string
  available: boolean
  description: string
}

export interface AIModelsResponse {
  default: string
  current: string
  models: Record<string, AIModelInfo>
}

export interface AIModelUpdateRequest {
  model: AIModelProvider
}

// Prompt Management Types
export interface PromptVersion {
  id: string
  prompt_id: string
  version_number: number
  content: string
  change_notes?: string
  is_current: boolean
  created_by?: string
  created_at: string
}

export interface Prompt {
  id: string
  name: string
  display_name: string
  description?: string
  category: string
  use_case: string
  is_active: boolean
  created_by?: string
  created_at: string
  updated_at: string
}

export interface PromptListItem {
  id: string
  name: string
  display_name: string
  description?: string
  category: string
  use_case: string
  is_active: boolean
  version_count: number
  current_version_number?: number
  created_at: string
  updated_at: string
}

export interface PromptDetail extends Prompt {
  versions: PromptVersion[]
  current_version?: PromptVersion
}

export interface PromptCreate {
  name: string
  display_name: string
  description?: string
  category: string
  use_case?: string
  initial_content: string
}

export interface PromptUpdate {
  display_name?: string
  description?: string
  category?: string
  use_case?: string
  is_active?: boolean
}

export interface PromptVersionCreate {
  content: string
  change_notes?: string
}

export interface TemplatePromptMapping {
  id: string
  template_id: string
  prompt_id: string
  section_name: string
  display_order: number
  created_at: string
}

// Experiment Types
export type ExperimentStatusType = 'draft' | 'running' | 'paused' | 'completed'

export interface ExperimentVariant {
  id: string
  experiment_id: string
  variant_name: string
  prompt_version_id: string
  traffic_percentage: number
  created_at: string
  version_number?: number
  version_content?: string
}

export interface Experiment {
  id: string
  name: string
  description?: string
  prompt_id: string
  status: ExperimentStatusType
  start_date?: string
  end_date?: string
  created_by?: string
  created_at: string
  updated_at: string
}

export interface ExperimentListItem {
  id: string
  name: string
  description?: string
  prompt_id: string
  prompt_name?: string
  prompt_display_name?: string
  status: ExperimentStatusType
  variant_count: number
  total_executions: number
  start_date?: string
  end_date?: string
  created_at: string
}

export interface ExperimentDetail extends Experiment {
  prompt_name?: string
  prompt_display_name?: string
  variants: ExperimentVariant[]
  total_executions: number
}

export interface ExperimentCreate {
  name: string
  description?: string
  prompt_id: string
  variants: {
    variant_name: string
    prompt_version_id: string
    traffic_percentage: number
  }[]
}

export interface ExperimentUpdate {
  name?: string
  description?: string
  status?: ExperimentStatusType
}

export interface ExperimentStats {
  experiment_id: string
  total_executions: number
  variants: {
    variant_id: string
    variant_name: string
    executions: number
    actual_percentage: number
    configured_percentage: number
  }[]
}

// API Functions

// Projects
export const getProjects = () => api.get<Project[]>('/api/projects')
export const getProject = (id: string) => api.get<Project>(`/api/projects/${id}`)
export const createProject = (data: { customer_name: string; project_name: string; description?: string; workflow_type?: string }) =>
  api.post<Project>('/api/projects', data)
export const updateProject = (id: string, data: Partial<Project>) =>
  api.put<Project>(`/api/projects/${id}`, data)
export const deleteProject = (id: string) => api.delete(`/api/projects/${id}`)

// Recordings
export const getRecordings = (projectId?: string) =>
  api.get<Recording[]>('/api/recordings', { params: { project_id: projectId } })
export const getRecording = (id: string) => api.get<Recording>(`/api/recordings/${id}`)
export const uploadRecording = (formData: FormData) =>
  api.post<Recording>('/api/recordings/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
export const deleteRecording = (id: string) => api.delete(`/api/recordings/${id}`)

// Transcription
export const startTranscription = (recordingId: string) =>
  api.post<Recording>(`/api/transcription/${recordingId}/start`)
export const getTranscriptionStatus = (recordingId: string) =>
  api.get<Recording>(`/api/transcription/${recordingId}/status`)
export const getTranscript = (recordingId: string) =>
  api.get(`/api/recordings/${recordingId}/transcript`)
export const downloadTranscript = async (recordingId: string, filename: string) => {
  const response = await api.get(`/api/recordings/${recordingId}/transcript/download`, {
    responseType: 'blob',
  })
  const url = window.URL.createObjectURL(new Blob([response.data]))
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', filename)
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}

// Query
export const queryTranscripts = (data: { project_id: string; question: string; include_project_documents?: boolean }) =>
  api.post<QueryResponse>('/api/query', data)
export const getQueryHistory = (projectId: string) =>
  api.get<QueryResponse[]>(`/api/query/history/${projectId}`)

// Documents
export const getDocuments = (projectId?: string) =>
  api.get<Document[]>('/api/documents', { params: projectId ? { project_id: projectId } : undefined })
export const generateDocument = (data: { project_id: string; template_name: string; output_format: string; use_prompt_library?: boolean }) =>
  api.post<Document>('/api/documents/generate', data)
export const getDocumentDownload = (documentId: string) =>
  api.get<{ download_url: string; filename: string }>(`/api/documents/${documentId}/download`)
export const getDocumentBlob = async (documentId: string): Promise<Blob> => {
  const response = await api.get(`/api/documents/${documentId}/blob`, {
    responseType: 'blob',
  })
  return new Blob([response.data])
}
export const deleteDocument = (documentId: string) =>
  api.delete(`/api/documents/${documentId}`)
export const requestLegalReview = (documentId: string) =>
  api.post<Document>(`/api/documents/${documentId}/legal-review`)

// Templates
export const getTemplates = (includeInactive: boolean = false) =>
  api.get<TemplateListItem[]>('/api/templates', { params: { include_inactive: includeInactive } })
export const getTemplate = (id: string) => api.get<Template>(`/api/templates/${id}`)
export const getTemplateByName = (name: string) => api.get<Template>(`/api/templates/by-name/${name}`)
export const createTemplate = (data: TemplateCreate) => api.post<Template>('/api/templates', data)
export const uploadTemplate = (formData: FormData) =>
  api.post<Template>('/api/templates/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
export const updateTemplate = (id: string, data: Partial<Template>) =>
  api.put<Template>(`/api/templates/${id}`, data)
export const deleteTemplate = (id: string) => api.delete(`/api/templates/${id}`)
export const publishTemplate = (id: string) => api.post<TemplateListItem>(`/api/templates/${id}/publish`)
export const downloadTemplate = async (templateId: string, filename: string) => {
  const response = await api.get(`/api/templates/${templateId}/download`, {
    responseType: 'blob',
  })
  const url = window.URL.createObjectURL(new Blob([response.data]))
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', filename)
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}
export const getTemplateBlob = async (templateId: string): Promise<Blob> => {
  const response = await api.get(`/api/templates/${templateId}/download`, {
    responseType: 'blob',
  })
  return new Blob([response.data])
}
// AI-ify job types
export interface AiifyJobResponse {
  id: string
  original_filename: string
  status: 'pending' | 'processing' | 'completed' | 'error'
  error_message?: string
  total_sections: number
  processed_sections: number
  progress_percent: number
  created_at: string
  started_at?: string
  completed_at?: string
}

// Start an AI-ify job (returns immediately with job ID)
export const startAiifyJob = async (file: File): Promise<AiifyJobResponse> => {
  const formData = new FormData()
  formData.append('file', file)
  const response = await api.post<AiifyJobResponse>('/api/templates/ai-ify', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return response.data
}

// Get AI-ify job status
export const getAiifyJobStatus = async (jobId: string): Promise<AiifyJobResponse> => {
  const response = await api.get<AiifyJobResponse>(`/api/templates/ai-ify/status/${jobId}`)
  return response.data
}

// Download AI-ify result
export const downloadAiifyResult = async (jobId: string, filename: string): Promise<void> => {
  const response = await api.get(`/api/templates/ai-ify/download/${jobId}`, {
    responseType: 'blob',
  })
  const url = window.URL.createObjectURL(new Blob([response.data]))
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', filename)
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}

// List AI-ify jobs
export const listAiifyJobs = async (): Promise<AiifyJobResponse[]> => {
  const response = await api.get<AiifyJobResponse[]>('/api/templates/ai-ify/jobs')
  return response.data
}

// Teams Integration
export const getTeamsMeetings = (params?: { start_date?: string; end_date?: string; limit?: number }) =>
  api.get<TeamsMeeting[]>('/api/teams/meetings', { params })
export const getTeamsTranscripts = (meetingId: string, joinUrl?: string) =>
  api.get<TeamsTranscript[]>(`/api/teams/meetings/${meetingId}/transcripts`, { params: joinUrl ? { join_url: joinUrl } : undefined })
export const importTeamsTranscript = (data: { meeting_id: string; transcript_id: string; project_id: string; meeting_subject?: string }) =>
  api.post<TeamsImportResponse>('/api/teams/import', data)
export const lookupTeamsMeeting = (joinUrl: string) =>
  api.get('/api/teams/lookup', { params: { join_url: joinUrl } })

// Project Documents (Source Documents for RAG)
export const getProjectDocuments = (projectId: string) =>
  api.get<ProjectDocument[]>('/api/project-documents', { params: { project_id: projectId } })
export const getProjectDocument = (documentId: string) =>
  api.get<ProjectDocument>(`/api/project-documents/${documentId}`)
export const uploadProjectDocument = (formData: FormData) =>
  api.post<ProjectDocument>('/api/project-documents', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
export const deleteProjectDocument = (documentId: string) =>
  api.delete(`/api/project-documents/${documentId}`)
export const reprocessProjectDocument = (documentId: string) =>
  api.post<ProjectDocument>(`/api/project-documents/${documentId}/reprocess`)

// GeekMode
export const getGeekModeExecutions = (params?: { project_id?: string; document_id?: string; limit?: number }) =>
  api.get<PromptExecution[]>('/api/geekmode/executions', { params })
export const getGeekModeExecution = (executionId: string) =>
  api.get<PromptExecution>(`/api/geekmode/executions/${executionId}`)
export const getGeekModeStats = (params?: { project_id?: string; document_id?: string }) =>
  api.get<GeekModeStats>('/api/geekmode/stats', { params })

// AI Model Settings (GeekMode)
export const getAIModels = () =>
  api.get<AIModelsResponse>('/api/geekmode/ai-models')
export const updateAIModel = (model: AIModelProvider) =>
  api.put<{ message: string; model: string; note: string }>('/api/geekmode/ai-model', { model })

// Prompts
export const getPrompts = (params?: { category?: string; use_case?: string; include_inactive?: boolean }) =>
  api.get<PromptListItem[]>('/api/prompts', { params })
export const getPrompt = (promptId: string) =>
  api.get<PromptDetail>(`/api/prompts/${promptId}`)
export const getPromptByName = (name: string) =>
  api.get<PromptDetail>(`/api/prompts/by-name/${name}`)
export const createPrompt = (data: PromptCreate) =>
  api.post<Prompt>('/api/prompts', data)
export const updatePrompt = (promptId: string, data: PromptUpdate) =>
  api.put<Prompt>(`/api/prompts/${promptId}`, data)
export const deletePrompt = (promptId: string) =>
  api.delete(`/api/prompts/${promptId}`)

// Prompt Versions
export const getPromptVersions = (promptId: string) =>
  api.get<PromptVersion[]>(`/api/prompts/${promptId}/versions`)
export const getCurrentPromptVersion = (promptId: string) =>
  api.get<PromptVersion>(`/api/prompts/${promptId}/versions/current`)
export const createPromptVersion = (promptId: string, data: PromptVersionCreate) =>
  api.post<PromptVersion>(`/api/prompts/${promptId}/versions`, data)
export const setCurrentPromptVersion = (promptId: string, versionId: string) =>
  api.post(`/api/prompts/${promptId}/versions/${versionId}/set-current`)

// Template-Prompt Mappings
export const getTemplateMappings = (templateId: string) =>
  api.get<TemplatePromptMapping[]>(`/api/prompts/template/${templateId}/mappings`)
export const createTemplateMapping = (data: { template_id: string; prompt_id: string; section_name: string; display_order?: number }) =>
  api.post<TemplatePromptMapping>('/api/prompts/template/mappings', data)
export const deleteTemplateMapping = (mappingId: string) =>
  api.delete(`/api/prompts/template/mappings/${mappingId}`)

// Experiments
export const getExperiments = (params?: { prompt_id?: string; status?: ExperimentStatusType }) =>
  api.get<ExperimentListItem[]>('/api/experiments', { params })
export const getExperiment = (experimentId: string) =>
  api.get<ExperimentDetail>(`/api/experiments/${experimentId}`)
export const createExperiment = (data: ExperimentCreate) =>
  api.post<ExperimentDetail>('/api/experiments', data)
export const updateExperiment = (experimentId: string, data: ExperimentUpdate) =>
  api.put<ExperimentDetail>(`/api/experiments/${experimentId}`, data)
export const deleteExperiment = (experimentId: string) =>
  api.delete(`/api/experiments/${experimentId}`)

// Experiment Variants
export const addExperimentVariant = (experimentId: string, data: { variant_name: string; prompt_version_id: string; traffic_percentage: number }) =>
  api.post<ExperimentVariant>(`/api/experiments/${experimentId}/variants`, data)
export const deleteExperimentVariant = (experimentId: string, variantId: string) =>
  api.delete(`/api/experiments/${experimentId}/variants/${variantId}`)

// Experiment Status
export const startExperiment = (experimentId: string) =>
  api.post(`/api/experiments/${experimentId}/start`)
export const pauseExperiment = (experimentId: string) =>
  api.post(`/api/experiments/${experimentId}/pause`)
export const completeExperiment = (experimentId: string) =>
  api.post(`/api/experiments/${experimentId}/complete`)

// Experiment Stats
export const getExperimentStats = (experimentId: string) =>
  api.get<ExperimentStats>(`/api/experiments/${experimentId}/stats`)

// Best Practices Types
export type BestPracticeSourceType = 'curated' | 'web' | 'upload'

export interface BestPracticeChunk {
  id: string
  best_practice_id: string
  use_case: string
  chunk_index: number
  text: string
  token_count?: number
  created_at: string
}

export interface BestPracticeChunkWithScore extends BestPracticeChunk {
  similarity_score: number
  best_practice_title?: string
}

export interface BestPractice {
  id: string
  use_case: string
  title: string
  description?: string
  content: string
  source_type: BestPracticeSourceType
  source_url?: string
  is_active: boolean
  created_by?: string
  created_at: string
  updated_at: string
  chunks: BestPracticeChunk[]
}

export interface BestPracticeListItem {
  id: string
  use_case: string
  title: string
  description?: string
  source_type: BestPracticeSourceType
  is_active: boolean
  created_at: string
  chunk_count: number
}

export interface BestPracticeCreate {
  use_case: string
  title: string
  description?: string
  content: string
  source_type?: BestPracticeSourceType
  source_url?: string
}

export interface BestPracticeUpdate {
  title?: string
  description?: string
  content?: string
  use_case?: string
  source_url?: string
  is_active?: boolean
}

export interface BestPracticeSearchResult {
  chunks: BestPracticeChunkWithScore[]
  total_tokens: number
}

// Best Practices API
export const getBestPractices = (params?: { use_case?: string; include_inactive?: boolean }) =>
  api.get<BestPracticeListItem[]>('/api/best-practices', { params })
export const getBestPractice = (bestPracticeId: string, includeChunks: boolean = false) =>
  api.get<BestPractice>(`/api/best-practices/${bestPracticeId}`, { params: { include_chunks: includeChunks } })
export const createBestPractice = (data: BestPracticeCreate, autoChunk: boolean = true) =>
  api.post<BestPractice>('/api/best-practices', data, { params: { auto_chunk: autoChunk } })
export const updateBestPractice = (bestPracticeId: string, data: BestPracticeUpdate, rechunk: boolean = true) =>
  api.put<BestPractice>(`/api/best-practices/${bestPracticeId}`, data, { params: { rechunk } })
export const deleteBestPractice = (bestPracticeId: string) =>
  api.delete(`/api/best-practices/${bestPracticeId}`)
export const rechunkBestPractice = (bestPracticeId: string, params?: { chunk_size?: number; chunk_overlap?: number }) =>
  api.post(`/api/best-practices/${bestPracticeId}/rechunk`, null, { params })
export const searchBestPractices = (query: string, useCase?: string, limit: number = 5) =>
  api.post<BestPracticeSearchResult>('/api/best-practices/search', { query, use_case: useCase, limit })
export const getBestPracticeContext = (query: string, useCase?: string, maxTokens: number = 2000) =>
  api.get<{ context: string; query: string; use_case?: string }>('/api/best-practices/context/for-prompt', {
    params: { query, use_case: useCase, max_tokens: maxTokens }
  })

// GitHub Import Types
export interface GitHubImportRequest {
  owner: string
  repo: string
  use_case: string
  path?: string
  branch?: string
  max_files?: number
  github_token?: string
}

export interface GitHubImportResult {
  path: string
  title: string
  success: boolean
  error?: string
  best_practice_id?: string
  action?: 'created' | 'updated' | 'skipped'
}

export interface GitHubImportResponse {
  status: string
  total_files: number
  imported: number
  created: number
  updated: number
  failed: number
  results: GitHubImportResult[]
}

export interface GitHubScanResponse {
  owner: string
  repo: string
  branch: string
  path: string
  total_files: number
  files: string[]
}

// GitHub Import API
export const scanGitHubRepo = (request: GitHubImportRequest) =>
  api.post<GitHubScanResponse>('/api/best-practices/import/github/scan', request)
export const importFromGitHub = (request: GitHubImportRequest) =>
  api.post<GitHubImportResponse>('/api/best-practices/import/github', request)

// Web Import Types
export interface WebImportRequest {
  url: string
  use_case: string
  title?: string
  description?: string
}

export interface WebPreviewResponse {
  url: string
  title: string
  description?: string
  word_count: number
  content_preview: string
  success: boolean
  error?: string
}

export interface WebImportResponse {
  url: string
  title: string
  success: boolean
  best_practice_id?: string
  error?: string
}

export interface WebBatchImportRequest {
  urls: string[]
  use_case: string
}

export interface WebBatchImportResponse {
  status: string
  total_urls: number
  imported: number
  failed: number
  results: WebImportResponse[]
}

// WAF-CAF Audit Types
export type WafCafAuditStatus = 'uploaded' | 'extracting' | 'processing' | 'indexed' | 'error'

export interface WafCafAudit {
  id: string
  project_id: string
  filename: string
  original_filename: string
  blob_url: string
  file_size: number
  status: WafCafAuditStatus
  error_message?: string
  file_count: number
  chunk_count: number
  created_at: string
  updated_at: string
}

export interface WafCafAuditStats {
  audit_count: number
  chunk_count: number
}

export type AuditFileScope = 'findings' | 'all_subscriptions' | 'management_group' | 'subscription'

export interface WafCafAuditFile {
  path: string
  name: string
  scope: AuditFileScope
  scope_name: string
  subscription: string  // backwards compatibility
  category: string
  type: 'csv' | 'json' | 'md'
  row_count: number
  data: Record<string, unknown>[] | Record<string, unknown> | string  // string for markdown
}

export interface WafCafScopeData {
  files: WafCafAuditFile[]
  categories: Record<string, WafCafAuditFile[]>
}

export interface WafCafAuditData {
  audit_id: string
  filename: string
  // New structure
  findings: WafCafScopeData
  outputs: {
    all_subscriptions: WafCafAuditFile[]
    management_groups: Record<string, WafCafScopeData>
    subscriptions: Record<string, WafCafScopeData>
  }
  // Keep for backwards compatibility
  subscriptions: Record<string, Record<string, WafCafAuditFile[]>>
  categories: Record<string, WafCafAuditFile[]>
  files: WafCafAuditFile[]
  stats: {
    total_files: number
    findings_count: number
    management_group_count: number
    subscription_count: number
    categories: string[]
  }
}

// Web Import API
export const previewWebUrl = (request: WebImportRequest) =>
  api.post<WebPreviewResponse>('/api/best-practices/import/web/preview', request)
export const importFromWeb = (request: WebImportRequest) =>
  api.post<WebImportResponse>('/api/best-practices/import/web', request)
export const importBatchFromWeb = (request: WebBatchImportRequest) =>
  api.post<WebBatchImportResponse>('/api/best-practices/import/web/batch', request)

// WAF-CAF Audits
export const getWafCafAudits = (projectId: string) =>
  api.get<WafCafAudit[]>('/api/waf-caf-audits', { params: { project_id: projectId } })
export const getWafCafAudit = (auditId: string) =>
  api.get<WafCafAudit>(`/api/waf-caf-audits/${auditId}`)
export const getWafCafAuditStats = (projectId: string) =>
  api.get<WafCafAuditStats>('/api/waf-caf-audits/stats', { params: { project_id: projectId } })
export const uploadWafCafAudit = (formData: FormData) =>
  api.post<WafCafAudit>('/api/waf-caf-audits', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
export const deleteWafCafAudit = (auditId: string) =>
  api.delete(`/api/waf-caf-audits/${auditId}`)
export const getWafCafAuditData = (auditId: string) =>
  api.get<WafCafAuditData>(`/api/waf-caf-audits/${auditId}/data`)

// Remediation Types
export interface RemediationRequest {
  finding: string
  severity: string
  category: string
  resource_name?: string
  recommendation?: string
  subscription?: string
}

export interface RemediationResponse {
  finding: string
  severity: string
  category: string
  remediation_steps: string
  code_snippets?: {
    azure_cli?: string
    powershell?: string
    terraform?: string
  }
  best_practice_sources: string[]
  estimated_effort?: string
}

// Remediation API
export const generateRemediation = (auditId: string, request: RemediationRequest) =>
  api.post<RemediationResponse>(`/api/waf-caf-audits/${auditId}/remediate`, request)

// Remediation Task Types (Backlog Management)
export type TaskImpact = 'critical' | 'high' | 'medium' | 'low' | 'info'
export type TaskPriority = 'P1' | 'P2' | 'P3' | 'P4' | 'P5'
export type TaskComplexity = 'trivial' | 'simple' | 'moderate' | 'complex' | 'major'
export type TaskStatus = 'backlog' | 'todo' | 'in_progress' | 'blocked' | 'in_review' | 'completed' | 'deferred' | 'wont_fix'
export type TimeEstimate = '< 30 min' | '1 hour' | '2-4 hours' | '4-8 hours' | '1 day' | '2-3 days' | '1 week' | '2+ weeks'

export interface RemediationTask {
  id: string
  project_id: string
  audit_id: string
  finding_title: string
  finding_description: string
  category: string
  resource_name?: string
  subscription?: string
  source_file?: string
  impact: TaskImpact
  priority: TaskPriority
  complexity: TaskComplexity
  time_estimate: TimeEstimate
  risk_score?: number
  status: TaskStatus
  assignee?: string
  recommendation?: string
  remediation_steps?: string
  code_snippets?: {
    azure_cli?: string
    powershell?: string
    terraform?: string
  }
  notes?: string
  tags: string[]
  created_at: string
  updated_at: string
  due_date?: string
  completed_at?: string
}

export interface RemediationTaskCreate {
  project_id: string
  audit_id: string
  finding_title: string
  finding_description: string
  category: string
  resource_name?: string
  subscription?: string
  source_file?: string
  impact?: TaskImpact
  priority?: TaskPriority
  complexity?: TaskComplexity
  time_estimate?: TimeEstimate
  recommendation?: string
  remediation_steps?: string
  code_snippets?: Record<string, string>
  assignee?: string
  notes?: string
  tags?: string[]
  due_date?: string
}

export interface RemediationTaskUpdate {
  finding_title?: string
  finding_description?: string
  category?: string
  resource_name?: string
  subscription?: string
  impact?: TaskImpact
  priority?: TaskPriority
  complexity?: TaskComplexity
  time_estimate?: TimeEstimate
  status?: TaskStatus
  assignee?: string
  recommendation?: string
  remediation_steps?: string
  code_snippets?: Record<string, string>
  notes?: string
  tags?: string[]
  due_date?: string
}

export interface RemediationTaskListItem {
  id: string
  project_id: string
  audit_id: string
  finding_title: string
  category: string
  resource_name?: string
  subscription?: string
  impact: TaskImpact
  priority: TaskPriority
  complexity: TaskComplexity
  time_estimate: TimeEstimate
  risk_score?: number
  status: TaskStatus
  assignee?: string
  notes?: string
  tags: string[]
  created_at: string
  updated_at: string
  due_date?: string
}

export interface RemediationTaskStats {
  total: number
  by_status: Record<string, number>
  by_impact: Record<string, number>
  by_priority: Record<string, number>
  by_complexity: Record<string, number>
  by_category: Record<string, number>
  avg_risk_score?: number
  completion_rate: number
}

// Remediation Tasks API (Backlog)
export const getRemediationTasks = (params?: {
  project_id?: string
  audit_id?: string
  status?: TaskStatus
  impact?: TaskImpact
  priority?: TaskPriority
  category?: string
  assignee?: string
  search?: string
  sort_by?: string
  sort_order?: string
  limit?: number
  offset?: number
}) => api.get<RemediationTaskListItem[]>('/api/remediation-tasks', { params })

export const getRemediationTask = (taskId: string) =>
  api.get<RemediationTask>(`/api/remediation-tasks/${taskId}`)

export const createRemediationTask = (task: RemediationTaskCreate) =>
  api.post<RemediationTask>('/api/remediation-tasks', task)

export const updateRemediationTask = (taskId: string, update: RemediationTaskUpdate) =>
  api.put<RemediationTask>(`/api/remediation-tasks/${taskId}`, update)

export const deleteRemediationTask = (taskId: string) =>
  api.delete(`/api/remediation-tasks/${taskId}`)

export const duplicateRemediationTask = (taskId: string) =>
  api.post<RemediationTask>(`/api/remediation-tasks/${taskId}/duplicate`)

export const getRemediationTaskStats = (params?: { project_id?: string; audit_id?: string }) =>
  api.get<RemediationTaskStats>('/api/remediation-tasks/stats', { params })

export const exportRemediationTasksCsv = (params?: {
  project_id?: string
  audit_id?: string
  status?: TaskStatus
}) => api.get('/api/remediation-tasks/export/csv', {
  params,
  responseType: 'blob',
})

export const exportRemediationTasksMarkdown = (params?: {
  project_id?: string
  audit_id?: string
  status?: TaskStatus
  include_details?: boolean
}) => api.get('/api/remediation-tasks/export/markdown', {
  params,
  responseType: 'blob',
})

// Bulk import findings response
export interface BulkImportResponse {
  imported: number
  skipped: number
  total_findings: number
}

// Import all findings from an audit to the backlog
export const importFindingsToBacklog = (
  auditId: string,
  minSeverity: string = 'Low'
) => api.post<BulkImportResponse>(`/api/remediation-tasks/import/${auditId}`, null, {
  params: { min_severity: minSeverity },
})

// Bulk delete response
export interface BulkDeleteResponse {
  deleted: number
  failed: number
}

// Bulk delete tasks
export const bulkDeleteTasks = (taskIds: string[]) =>
  api.post<BulkDeleteResponse>('/api/remediation-tasks/bulk-delete', { task_ids: taskIds })

// Delete all tasks for an audit
export const deleteAllTasksForAudit = (auditId: string) =>
  api.delete<{ deleted: number; total: number }>(`/api/remediation-tasks/audit/${auditId}/all`)

export default api
